/**
 * Design tokens — single source of truth for all visual decisions.
 * See DESIGN.md for rationale behind each choice.
 */

import { getLetterGrade } from '@privisy/ui-bridge';

export const T = {
  // Typography
  // Headlines at 20px and up only (#1936); smaller text uses fontBody.
  fontDisplay: "'Source Serif 4', Georgia, 'Times New Roman', serif",
  fontBody: "'Switzer', -apple-system, sans-serif",
  fontMono: "'JetBrains Mono', 'Fira Code', monospace",

  // Brand accent: cobalt, the same values as www/src/lib/tokens.ts (DESIGN.md
  // "Brand accent", #1934). 300/900/950 are the Tailwind blue steps www has no
  // cobalt name for: links on the dark band and the policy-analysis hero.
  cobalt50: '#eff6ff',
  cobalt100: '#dbeafe',
  cobalt200: '#bfdbfe',
  cobalt300: '#93c5fd',
  cobalt400: '#60a5fa',
  cobalt500: '#3b82f6',
  cobalt600: '#2563eb',
  cobalt700: '#1d4ed8',
  cobalt900: '#1e3a8a',
  cobalt950: '#172554',

  // Neutrals (Cool Slate)
  gray50: '#f8fafc',
  gray100: '#f1f5f9',
  gray200: '#e2e8f0',
  gray300: '#cbd5e1',
  gray400: '#94a3b8',
  gray500: '#64748b',
  gray600: '#475569',
  gray700: '#334155',
  gray800: '#1e293b',
  gray900: '#0f172a',

  // Semantic
  success: '#059669',
  successBg: '#ecfdf5',
  successBorder: '#a7f3d0',
  warning: '#d97706',
  warningBg: '#fffbeb',
  // Warning-status badge text: T.warning on warningBg reads 3.07:1; this
  // reaches 4.84:1 (#1663). T.warning stays for borders, dots and fills.
  warningText: '#b45309',
  warningBorder: '#fde68a',
  error: '#dc2626',
  errorBg: '#fef2f2',
  errorBorder: '#fecaca',
  // Equal to cobalt600 on purpose: neutral notices from Privisy only, never
  // severity or status (DESIGN.md "Semantic").
  info: '#2563eb',
  infoBg: '#eff6ff',
  infoBorder: '#bfdbfe',

  // Letter grades (DESIGN.md "Grade Colors") — foreground hue + badge pill background
  gradeA: '#059669',
  gradeABg: '#d1fae5',
  gradeB: '#ca8a04',
  gradeBBg: '#fef3c7',
  gradeC: '#ea580c',
  gradeCBg: '#ffedd5',
  gradeD: '#dc2626',
  gradeDBg: '#fee2e2',
  gradeF: '#991b1b',
  gradeFBg: '#fee2e2',
  // Badge text on the grade pill. The hues above are ring and fill colors and
  // read 2.64-3.95:1 as 12px text on their own pill; every ink clears 4.5:1.
  gradeAInk: '#047857',
  gradeBInk: '#854d0e',
  gradeCInk: '#9a3412',
  gradeDInk: '#b91c1c',
  gradeFInk: '#991b1b',

  // Extended palette — Tailwind-scale values promoted from formerly hardcoded
  // literals (#700). Prefer the semantic tokens above for new work; these
  // exist so every color in the dashboard resolves to a named token.
  white: '#ffffff',
  black: '#000000',
  emerald300: '#6ee7b7',
  emerald400: '#34d399',
  emerald500: '#10b981',
  emerald700: '#047857',
  emerald800: '#065f46',
  green50: '#f0fdf4',
  green100: '#dcfce7',
  green200: '#bbf7d0',
  green400: '#4ade80',
  green500: '#22c55e',
  green600: '#16a34a',
  green700: '#15803d',
  green800: '#166534',
  teal100: '#ccfbf1',
  teal300: '#5eead4',
  teal700: '#0f766e',
  sky100: '#e0f2fe',
  sky600: '#0284c7',
  blue300: '#93c5fd',
  blue500: '#3b82f6',
  blue700: '#1d4ed8',
  blue800: '#1e40af',
  // Violet is AI Crosscheck (tinted, never filled) and the Preferences/Social
  // data labels (DESIGN.md "AI Crosscheck").
  violet50: '#f5f3ff',
  violet200: '#ddd6fe',
  violet600: '#7c3aed',
  violet700: '#6d28d9',
  violet800: '#5b21b6',
  amber400: '#fbbf24',
  amber500: '#f59e0b',
  amber700: '#b45309',
  amber800: '#92400e',
  yellow50: '#fefce8',
  yellow100: '#fef9c3',
  yellow300: '#fde047',
  yellow700: '#a16207',
  yellow800: '#854d0e',
  orange50: '#fff7ed',
  orange300: '#fdba74',
  orange700: '#c2410c',
  orange900: '#7c2d12',
  red300: '#fca5a5',
  red400: '#f87171',
  red500: '#ef4444',
  red700: '#b91c1c',

  // The only dark ground left in the dashboard (#1937).
  /** gray-900 at 85%: the lightbox backdrop (Scan screenshots, epic #1331) */
  scrim: 'rgba(15, 23, 42, 0.85)',

  // On-dark overlays: white at a fixed opacity, for hairlines, skeleton
  // blocks and hover fills on the dark surfaces (#1980). 0.05 and 0.06 were
  // indistinguishable and merged into overlayFaint.
  overlayFaint: 'rgba(255, 255, 255, 0.06)',
  overlaySoft: 'rgba(255, 255, 255, 0.08)',
  overlayLine: 'rgba(255, 255, 255, 0.1)',

  // Elevation (DESIGN.md "Elevation", #1980)
  shadowSm: '0 1px 3px rgba(0, 0, 0, 0.1)',
  shadowMd: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  shadowLg: '0 20px 60px -10px rgba(0, 0, 0, 0.3)',

  // Layout
  contentWidth: 880,

  // Radii
  radiusSm: 4,
  radiusMd: 6,
  radiusLg: 10,
  radiusXl: 12,
  radiusFull: 9999,
} as const;

// Severity colors — deliberately darker than semantic colors. Everything below
// medium is slate ink, so low never reads as a cobalt link (#1934).
/**
 * A palette token at the given opacity, as `rgba()`. Every translucent colour
 * stays tied to a token instead of a raw literal the hex gate can't see (#1978).
 */
export function withAlpha(token: string, alpha: number): string {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(token);
  if (!m) throw new Error(`withAlpha: expected a hex token, got ${token}`);
  if (!(alpha >= 0 && alpha <= 1)) throw new Error(`withAlpha: alpha ${alpha} is outside 0..1`);
  const hex = m[1].length === 3 ? [...m[1]].map((c) => c + c).join('') : m[1];
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const SEVERITY_COLOR: Record<string, string> = {
  critical: T.gradeF,
  high: T.orange700,
  medium: T.yellow700,
  low: T.gray700,
  info: T.gray500,
};

export const SEVERITY_BG: Record<string, string> = {
  critical: T.errorBg,
  high: T.orange50,
  medium: T.yellow50,
  low: T.gray50,
  info: T.gray50,
};

export const SEVERITY_BORDER: Record<string, string> = {
  critical: T.red300,
  high: T.orange300,
  medium: T.yellow300,
  low: T.gray600,
  info: T.gray300,
};

export const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

export interface Grade {
  letter: 'A' | 'B' | 'C' | 'D' | 'F';
  color: string;
  bg: string;
}

// The single grade→color mapping (bands per DESIGN.md "Grade Colors").
// Every consumer of a grade letter, hue, or badge background derives from
// here; the score→letter ladder itself lives in @privisy/ui-bridge so the
// dashboard and the www scan widget can never disagree (issue #722).
const GRADE_PALETTE: Record<Grade['letter'], Pick<Grade, 'color' | 'bg'>> = {
  A: { color: T.gradeA, bg: T.gradeABg },
  B: { color: T.gradeB, bg: T.gradeBBg },
  C: { color: T.gradeC, bg: T.gradeCBg },
  D: { color: T.gradeD, bg: T.gradeDBg },
  F: { color: T.gradeF, bg: T.gradeFBg },
};

export function gradeFor(score: number): Grade {
  const letter = getLetterGrade(score);
  return { letter, ...GRADE_PALETTE[letter] };
}

const GRADE_INK: Record<Grade['letter'], string> = {
  A: T.gradeAInk,
  B: T.gradeBInk,
  C: T.gradeCInk,
  D: T.gradeDInk,
  F: T.gradeFInk,
};

/** Text color for a grade badge drawn on gradeFor(score).bg. */
export function gradeInkFor(score: number): string {
  return GRADE_INK[getLetterGrade(score)];
}

// A scan that assessed nothing gets no letter (see isUngraded in @privisy/api-types).
export { isUngraded } from '@privisy/api-types';

export const UNGRADED = { letter: 'N/A', color: T.gray500, bg: T.gray100 } as const;

// Grade colors by score
export function gradeColor(score: number): string {
  return gradeFor(score).color;
}

// Canonical status labels for the dashboard
export const STATUS_LABELS = {
  compliant: 'Compliant',
  non_compliant: 'Non-Compliant',
  warning: 'Needs Attention',
  error: 'Stage Failed',
  skipped: 'Not Tested',
} as const;

// Centralized status badge/dot color mapping
export const STATUS_COLORS = {
  compliant: { bg: T.successBg, text: T.success, border: T.successBorder },
  non_compliant: { bg: T.errorBg, text: T.error, border: T.errorBorder },
  warning: { bg: T.warningBg, text: T.warningText, border: T.warningBorder },
  error: { bg: T.errorBg, text: T.gradeF, border: T.red300 }, // distinct error styling, darker red
  skipped: { bg: T.gray50, text: T.gray500, border: T.gray200 },
} as const;

// Section metadata with numbering. `icon` merged in from the report-local
// copy of this map (#990) — the report surface renders `number`/`label`
// today, but the shape stays complete so either surface can use any field.
export const SECTION_META: Record<string, { label: string; number: string; icon: string }> = {
  uiCompliance: { label: 'UI Compliance', number: '01', icon: '⚡' },
  trackerDetection: { label: 'Tracker Detection', number: '02', icon: '🍪' },
  gpcValidation: { label: 'GPC Signal', number: '03', icon: '🛡' },
  privacyPolicy: { label: 'Privacy Policy', number: '04', icon: '📄' },
};

// Tracker category colors
export const TRACKER_CATEGORY: Record<string, { label: string; color: string; bg: string }> = {
  marketing: { label: 'Marketing', color: T.error, bg: T.errorBg },
  social_media: { label: 'Social', color: T.violet600, bg: T.violet50 },
  analytics: { label: 'Analytics', color: T.info, bg: T.infoBg },
  functional: { label: 'Functional', color: T.success, bg: T.successBg },
  strictly_necessary: { label: 'Necessary', color: T.success, bg: T.successBg },
  unknown: { label: 'Unknown', color: T.gray500, bg: T.gray50 },
};

// Cookie report category colors (the five cookie-report buckets)
export const COOKIE_CATEGORY: Record<string, { label: string; color: string; bg: string }> = {
  strictly_necessary: { label: 'Strictly Necessary', color: T.success, bg: T.successBg },
  marketing: { label: 'Marketing', color: T.error, bg: T.errorBg },
  analytics: { label: 'Analytics', color: T.info, bg: T.infoBg },
  preferences: { label: 'Preferences', color: T.violet600, bg: T.violet50 },
  uncategorized: { label: 'Uncategorized', color: T.gray500, bg: T.gray50 },
};
